import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiPlus, 
  FiEdit3, 
  FiTrash2, 
  FiUsers, 
  FiEye, 
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiShield,
  FiUser,
  FiMail,
  FiClock,
  FiLock,
  FiUnlock
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { userService } from '../../services/api';
import './Usuarios.css';

const Usuarios = () => {
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroPermissao, setFiltroPermissao] = useState('todos');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [usuarioParaExcluir, setUsuarioParaExcluir] = useState(null);
  const [mostrarModalExclusao, setMostrarModalExclusao] = useState(false);

  useEffect(() => {
    carregarUsuarios();
  }, []);

  const carregarUsuarios = async () => {
    try {
      setLoading(true);
      const response = await userService.getAll();
      if (response.success) {
        setUsuarios(response.data || []);
      } else {
        toast.error('Erro ao carregar usuários');
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const confirmarExclusao = (usuario) => {
    setUsuarioParaExcluir(usuario);
    setMostrarModalExclusao(true);
  };

  const excluirUsuario = async () => {
    if (!usuarioParaExcluir) return;

    try {
      const response = await userService.delete(usuarioParaExcluir.id);
      if (response.success) {
        toast.success('Usuário excluído com sucesso!');
        carregarUsuarios();
      } else {
        toast.error(response.message || 'Erro ao excluir usuário');
      }
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      toast.error('Erro ao excluir usuário');
    } finally {
      setMostrarModalExclusao(false);
      setUsuarioParaExcluir(null);
    }
  };

  const toggleStatus = async (usuario) => {
    try {
      const dadosAtualizados = {
        ...usuario,
        ativo: !usuario.ativo
      };

      const response = await userService.update(usuario.id, dadosAtualizados);
      if (response.success) {
        toast.success(`Usuário ${usuario.ativo ? 'desativado' : 'ativado'} com sucesso!`);
        carregarUsuarios();
      } else {
        toast.error(response.message || 'Erro ao alterar status do usuário');
      }
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      toast.error('Erro ao alterar status do usuário');
    }
  };

  const usuariosFiltrados = usuarios.filter(usuario => {
    const matchSearch = usuario.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       usuario.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchPermissao = filtroPermissao === 'todos' || usuario.permissao === filtroPermissao;
    const matchStatus = filtroStatus === 'todos' || 
                       (filtroStatus === 'ativo' && usuario.ativo) ||
                       (filtroStatus === 'inativo' && !usuario.ativo);
    
    return matchSearch && matchPermissao && matchStatus;
  });

  const getPermissaoInfo = (permissao) => {
    const permissoes = {
      'admin': { label: 'Administrador', icon: FiShield, classe: 'admin' },
      'editor': { label: 'Editor', icon: FiEdit3, classe: 'editor' },
      'viewer': { label: 'Visualizador', icon: FiEye, classe: 'viewer' },
      'auxiliar': { label: 'Auxiliar de Jovens', icon: FiUser, classe: 'auxiliar' }
    };
    return permissoes[permissao] || { label: permissao, icon: FiUser, classe: 'default' };
  };

  const formatarDataUltimoLogin = (data) => {
    if (!data) return 'Nunca acessou';
    return new Date(data).toLocaleDateString('pt-BR') + ' às ' + 
           new Date(data).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <FiRefreshCw className="spin" />
          <p>Carregando usuários...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="usuarios-page">
      <div className="page-header">
        <div className="header-content">
          <h1>
            <FiUsers className="page-icon" />
            Gerenciar Usuários
          </h1>
          <p>Controle de acesso e permissões do sistema</p>
        </div>
        <button
          className="btn-primary"
          onClick={() => navigate('/usuarios/cadastro')}
        >
          <FiPlus /> Novo Usuário
        </button>
      </div>

      <div className="page-filters">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Buscar por nome ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filters-row">
          <div className="filter-group">
            <FiFilter className="filter-icon" />
            <select 
              value={filtroPermissao} 
              onChange={(e) => setFiltroPermissao(e.target.value)}
            >
              <option value="todos">Todas Permissões</option>
              <option value="admin">Administradores</option>
              <option value="editor">Editores</option>
              <option value="auxiliar">Auxiliares de Jovens</option>
              <option value="viewer">Visualizadores</option>
            </select>
          </div>

          <div className="filter-group">
            <select 
              value={filtroStatus} 
              onChange={(e) => setFiltroStatus(e.target.value)}
            >
              <option value="todos">Todos Status</option>
              <option value="ativo">Ativos</option>
              <option value="inativo">Inativos</option>
            </select>
          </div>
        </div>

        <button
          className="btn-refresh"
          onClick={carregarUsuarios}
          title="Atualizar lista"
        >
          <FiRefreshCw />
        </button>
      </div>

      <div className="usuarios-stats">
        <div className="stat-card">
          <span className="stat-number">{usuarios.length}</span>
          <span className="stat-label">Total</span>
        </div>
        <div className="stat-card active">
          <span className="stat-number">{usuarios.filter(u => u.ativo).length}</span>
          <span className="stat-label">Ativos</span>
        </div>
        <div className="stat-card admin">
          <span className="stat-number">{usuarios.filter(u => u.permissao === 'admin').length}</span>
          <span className="stat-label">Admins</span>
        </div>
        <div className="stat-card editor">
          <span className="stat-number">{usuarios.filter(u => u.permissao === 'editor').length}</span>
          <span className="stat-label">Editores</span>
        </div>
        <div className="stat-card">
          <span className="stat-number">{usuariosFiltrados.length}</span>
          <span className="stat-label">Filtrados</span>
        </div>
      </div>

      {usuariosFiltrados.length === 0 ? (
        <div className="empty-state">
          <FiUsers className="empty-icon" />
          <h3>Nenhum usuário encontrado</h3>
          <p>
            {searchTerm || filtroPermissao !== 'todos' || filtroStatus !== 'todos'
              ? 'Tente ajustar os filtros de busca'
              : 'Comece cadastrando o primeiro usuário'
            }
          </p>
          {!searchTerm && filtroPermissao === 'todos' && filtroStatus === 'todos' && (
            <button
              className="btn-primary"
              onClick={() => navigate('/usuarios/cadastro')}
            >
              <FiPlus /> Cadastrar Primeiro Usuário
            </button>
          )}
        </div>
      ) : (
        <div className="usuarios-grid">
          {usuariosFiltrados.map(usuario => {
            const permissaoInfo = getPermissaoInfo(usuario.permissao);
            const PermissaoIcon = permissaoInfo.icon;
            
            return (
              <div key={usuario.id} className={`usuario-card ${!usuario.ativo ? 'inactive' : ''}`}>
                <div className="usuario-header">
                  <div className="usuario-avatar">
                    <FiUser className="avatar-icon" />
                  </div>
                  <div className="usuario-basic">
                    <h3>{usuario.nome}</h3>
                    <div className="usuario-email">
                      <FiMail />
                      <span>{usuario.email}</span>
                    </div>
                  </div>
                  <div className="usuario-actions">
                    <button
                      className="btn-icon edit"
                      onClick={() => navigate(`/usuarios/editar/${usuario.id}`)}
                      title="Editar"
                    >
                      <FiEdit3 />
                    </button>
                    <button
                      className="btn-icon delete"
                      onClick={() => confirmarExclusao(usuario)}
                      title="Excluir"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
                
                <div className="usuario-body">
                  <div className="permissao-badge">
                    <PermissaoIcon />
                    <span>{permissaoInfo.label}</span>
                  </div>
                  
                  <div className="usuario-info">
                    <div className="info-item">
                      <FiClock />
                      <div className="info-content">
                        <span className="info-label">Último acesso:</span>
                        <span className="info-value">{formatarDataUltimoLogin(usuario.ultimoLogin)}</span>
                      </div>
                    </div>
                    
                    <div className="status-info">
                      <span className={`status-badge ${usuario.ativo ? 'active' : 'inactive'}`}>
                        {usuario.ativo ? (
                          <><FiUnlock /> Ativo</>
                        ) : (
                          <><FiLock /> Inativo</>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="usuario-footer">
                  <button
                    className={`btn-toggle ${usuario.ativo ? 'deactivate' : 'activate'}`}
                    onClick={() => toggleStatus(usuario)}
                  >
                    {usuario.ativo ? (
                      <><FiLock /> Desativar</>
                    ) : (
                      <><FiUnlock /> Ativar</>
                    )}
                  </button>
                  
                  <small className="created-date">
                    Desde: {usuario.createdAt ? new Date(usuario.createdAt).toLocaleDateString('pt-BR') : '—'}
                  </small>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {mostrarModalExclusao && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirmar Exclusão</h3>
            </div>
            <div className="modal-body">
              <p>
                Tem certeza que deseja excluir o usuário <strong>{usuarioParaExcluir?.nome}</strong>?
              </p>
              <p className="warning">
                ⚠️ Esta ação não pode ser desfeita. O usuário perderá acesso ao sistema 
                permanentemente e todos os registros associados poderão ser afetados.
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  setMostrarModalExclusao(false);
                  setUsuarioParaExcluir(null);
                }}
              >
                Cancelar
              </button>
              <button
                className="btn-danger"
                onClick={excluirUsuario}
              >
                <FiTrash2 /> Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Usuarios; 